import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetService } from "./useGetService";
import { API, DashboardTabs } from "../constants";

// Third party
// import { v4 as uuidv4 } from "uuid";
import _ from "lodash";
import { getUserData } from "../shared/utils";
import {
  EServiceRequestCategory,
  IServiceRequest,
  ITmResponse,
} from "../types/global.types";
import { useArrivalDepartureData } from "./useArrivalDepartureData";

export const useVendorDataGrid = (
  startDate: Date,
  endDate: Date,
  selectedTab: string
) => {
  const [rows, setRows] = useState([] as any);
  const [isRowsLoading, setRowsLoading] = useState(false);
  const vendorId = getUserData()["cognito:username"];

  // API CALL

  const { response, isLoading, handleSearchApply, refreshingText, error } =
    useArrivalDepartureData({
      startDate,
      endDate,
      selectedTab,
    });

  // FETCH SHIPBOARD HR REQUEST DETAILS
  const { response: shipboardAssigned, isLoading: isShipboardAssignedLoading } =
    useGetService(`${API.VENDOR_SERVICE_REQUEST.SHIPBOARD_HR_ARRIVED}s`);

  // FETCH CREATED MANUAL REQUEST DETAILS
  const {
    response: manualRequestDetails,
    isLoading: ismanualRequestDetailsLoading,
  } = useGetService(
    `${API.VENDOR_SERVICE_REQUEST.CREATE_MANUAL_REQUEST}s?vendorId=${vendorId}`
  );

  // FETCH TRANSPORT ASSIGNMENT
  const {
    response: serviceRequests,
    isLoading: isAssignedDataLoading,
    refetch: refetchAssinged,
  } = useGetService(
    `${API.CARNIVAL_ADMIN.GET_SERVICE_REQUEST_BY_VENDOR_ID}?vendorId=${vendorId}`
  );

  const isDeparture = selectedTab === DashboardTabs.DEPARTURE;
  const assignedData = useMemo(() => {
    if (isAssignedDataLoading || !Array.isArray(serviceRequests?.data)) {
      return { data: [] };
    }

    const filteredData = serviceRequests.data.filter((x: IServiceRequest) =>
      isDeparture
        ? x.requestCategory === EServiceRequestCategory.DEPARTURE
        : x.requestCategory === EServiceRequestCategory.ARRIVAL
    );

    return { data: filteredData };
  }, [serviceRequests, isAssignedDataLoading, isDeparture]);

  const dataHandling = useCallback(
    (resData: ITmResponse[]) => {
      let newData: any[] = [];
      const data = _.cloneDeep(resData);

      data.forEach((x) => {
        const y = {
          ...x,
          id: x.EmpId, //uuidv4(),
          isManualRequestCreated:
            (!ismanualRequestDetailsLoading &&
              manualRequestDetails?.data &&
              manualRequestDetails?.data.some(
                (v: any) => v.tmId === x.EmpId
              )) ||
            false,
          assignedInfo:
            !isAssignedDataLoading &&
            assignedData?.data &&
            assignedData?.data.find((v: any) => v.tmId === x.EmpId),
        };

        // This code is temp once real data will come then need to remove the code
        const isShipboardScanned =
          !isShipboardAssignedLoading &&
          shipboardAssigned?.data &&
          shipboardAssigned?.data.every((v: any) => v.tmId !== x.EmpId);

        isShipboardScanned && newData.push(y);
      });

      let finalData: any[] = [];

      if (
        selectedTab === DashboardTabs.ARRIVALS ||
        selectedTab === DashboardTabs.DEPARTURE
      ) {
        finalData = newData.filter(
          (x: any) => x.isManualRequestCreated === false
        );
        setRowsLoading(false);
        return finalData;
      }

      if (
        selectedTab === DashboardTabs.TRANSPORTATION_REQUEST ||
        selectedTab === DashboardTabs.MANUAL_REQUEST
      ) {
        finalData = newData.filter(
          (x: any) => x.isManualRequestCreated === true
        );
        setRowsLoading(false);
        return finalData;
      }

      setRowsLoading(false);
      return newData;
    },
    [
      selectedTab,
      ismanualRequestDetailsLoading,
      manualRequestDetails,
      isAssignedDataLoading,
      assignedData,
      isShipboardAssignedLoading,
      shipboardAssigned,
    ]
  );

  useEffect(() => {
    setRowsLoading(isLoading || isAssignedDataLoading);

    if (!isLoading && Array.isArray(response?.data)) {
      const processedData = dataHandling(response.data);
      setRows(processedData as any);
    }

    // Optional: handle case where response is empty
    if (!isLoading && !response?.data?.length) {
      setRows([]); // explicitly clear if no result
    }
  }, [
    response,
    isLoading,
    assignedData,
    isAssignedDataLoading,
    isShipboardAssignedLoading,
    shipboardAssigned,
    ismanualRequestDetailsLoading,
    manualRequestDetails,
    dataHandling,
  ]);

  const refetchData = (seltab: string) => {
    if (seltab === DashboardTabs.ARRIVALS) {
      refetchAssinged(
        `${API.CARNIVAL_ADMIN.GET_SERVICE_REQUEST_BY_VENDOR_ID}?vendorId=${vendorId}`
      );
    }
    if (seltab === DashboardTabs.DEPARTURE) {
      refetchAssinged(
        `${API.CARNIVAL_ADMIN.GET_SERVICE_REQUEST_BY_VENDOR_ID}?vendorId=${vendorId}`
      );
    }

    if (
      seltab === DashboardTabs.TRANSPORTATION_REQUEST ||
      seltab === DashboardTabs.MANUAL_REQUEST
    ) {
      refetchAssinged(
        `${API.CARNIVAL_ADMIN.GET_SERVICE_REQUEST_BY_VENDOR_ID}?vendorId=${vendorId}`
      );
    }
  };

  return {
    rows,
    isRowsLoading,
    refetchData,
    handleSearchApply,
    refreshingText,
    error,
  };
};
